/**
 * Story HTML Generator Utility
 * Extracted from Compiler.jsx for better maintainability
 * 
 * This module handles the generation of standalone HTML files from Twee format stories.
 */

/**
 * Parse Twee format into passages array
 */
export const parseTwee = (twee) => {
  const passages = [];
  const lines = twee.split('\n');
  let currentPassage = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.trim().startsWith(':: ')) {
      if (currentPassage && currentPassage.content.trim()) {
        passages.push(currentPassage);
      }
      const name = line.substring(3).split('[')[0].trim();
      currentPassage = { name, content: '' };
    } else if (currentPassage) {
      currentPassage.content += line + '\n';
    }
  }
  
  if (currentPassage && currentPassage.content.trim()) {
    passages.push(currentPassage);
  }
  
  return passages;
};

/**
 * Split long passages into smaller chunks with continuation links
 */
export const splitLongPassages = (passages, maxLength) => {
  if (maxLength === 0) return passages;
  
  const result = [];
  
  passages.forEach(passage => {
    if (passage.name === 'StoryTitle' || passage.content.length <= maxLength) {
      result.push(passage);
      return;
    }
    
    const paragraphs = passage.content.split(/\n\n+/);
    let currentChunk = '';
    const chunks = [];
    
    paragraphs.forEach((para, i) => {
      const testChunk = currentChunk + (currentChunk ? '\n\n' : '') + para;
      
      if (testChunk.length > maxLength && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = para;
      } else {
        currentChunk = testChunk;
      }
      
      if (i === paragraphs.length - 1 && currentChunk) {
        chunks.push(currentChunk);
      }
    });
    
    chunks.forEach((chunk, idx) => {
      const isLastChunk = idx === chunks.length - 1;
      const chunkName = idx === 0 ? passage.name : `${passage.name}_cont${idx}`;
      
      let chunkContent = chunk.trim();
      
      if (!isLastChunk) {
        const nextChunkName = `${passage.name}_cont${idx + 1}`;
        chunkContent += `\n\n[[Continue reading...→${nextChunkName}]]`;
      }
      
      result.push({
        name: chunkName,
        content: chunkContent
      });
    });
  });
  
  return result;
};

/**
 * Process content with Twee/SugarCube/Markdown formatting
 */
const processContent = (text) => {
  let processed = text;
  
  // Process Twee image syntax
  processed = processed.replace(/\[img\[([^\]|]+)(?:\|([^\]]+))?\]\]/gi, (match, url, position) => {
    const pos = position ? position.toLowerCase().trim() : 'center';
    return `<img src="${url}" alt="Story image" class="story-image img-${pos}">`;
  });
  
  // Process Markdown images
  processed = processed.replace(/!\[([^\]]*)\]\(([^)|]+)(?:\|([^)]+))?\)/gi, (match, alt, url, position) => {
    const pos = position ? position.toLowerCase().trim() : 'center';
    return `<img src="${url}" alt="${alt}" class="story-image img-${pos}">`;
  });
  
  // Horizontal rules: convert --- or *** to <hr>
  processed = processed.replace(/^[\-\*]{3,}$/gm, '<hr>');
  
  // Convert plain URLs to clickable links (before other processing)
  // Match URLs that aren't already in HTML tags or markdown links
  // Use onclick with window.open to avoid blob URL restrictions
  processed = processed.replace(
    /(?<!["\(])(https?:\/\/[^\s<>\[\]"'\)]+)/g,
    '<a href="$1" onclick="event.preventDefault(); window.open(\'$1\', \'_blank\');" style="color: #667eea; text-decoration: underline; cursor: pointer;">$1</a>'
  );
  
  // Mask <a>...</a> blocks so subsequent Markdown substitutions (especially the
  // _italic_ regex below) don't mangle URLs containing underscores or attribute
  // values like target="_blank". Restored at the end of this function.
  const __anchorMasks = [];
  processed = processed.replace(/<a\s[^>]*>[\s\S]*?<\/a>/gi, (m) => {
    __anchorMasks.push(m);
    return `\u0000ANC${__anchorMasks.length - 1}\u0000`;
  });
  
  // Convert bullet lists BEFORE bold/italic to avoid conflicts
  const lines = processed.split('\n');
  let inList = false;
  let result = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^[\*\-]\s+(.+)$/);
    
    if (bulletMatch && !line.match(/^[\*\-]{2,}/)) {
      if (!inList) {
        result.push('<ul>');
        inList = true;
      }
      result.push(`<li>${bulletMatch[1]}</li>`);
    } else {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      result.push(line);
    }
  }
  if (inList) {
    result.push('</ul>');
  }
  processed = result.join('\n');
  
  // SUGARCUBE SYNTAX (PRIMARY) - Bold: **text**
  processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // SUGARCUBE SYNTAX - Italic: *text* (not in lists or strong tags)
  processed = processed.replace(/(?<!<li>|<strong>)\*([^*\n]+)\*(?!<\/strong>)/g, '<em>$1</em>');
  processed = processed.replace(/(?<!<strong>)_([^_\n]+)_(?!<\/strong>)/g, '<em>$1</em>');
  
  // SUGARCUBE SYNTAX - Strikethrough: ~~text~~
  processed = processed.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  
  // SUGARCUBE SYNTAX - Headings: !Heading (SugarCube native)
  processed = processed.replace(/^!{1,6}\s*(.+)$/gm, (match, text) => {
    const level = match.match(/^!+/)[0].length;
    return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
  });
  
  // MARKDOWN SYNTAX - Headings: # text (also supported)
  processed = processed.replace(/^#{1,6}\s+(.+)$/gm, (match, text) => {
    const level = match.match(/^#+/)[0].length;
    return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
  });
  
  // LEGACY TWINE 1 SYNTAX (BACKWARD COMPATIBILITY) - Bold: ''text''
  processed = processed.replace(/''([^']+)''/g, '<strong>$1</strong>');
  
  // LEGACY TWINE 1 SYNTAX - Italic: //text// (but NOT in URLs like https://)
  processed = processed.replace(/(?<!:)\/\/([^/\n]+)\/\//g, '<em>$1</em>');
  
  // LEGACY TWINE 1 SYNTAX - Underline: __text__ (when not used for italic)
  processed = processed.replace(/__([^_]+)__/g, '<u>$1</u>');
  
  // LEGACY TWINE 1 SYNTAX - Strikethrough: ==text==
  processed = processed.replace(/==([^=]+)==/g, '<del>$1</del>');
  
  // Clean up excessive blank lines
  processed = processed.replace(/\n{4,}/g, '\n\n\n');
  
  // Convert newlines to breaks smartly
  processed = processed.replace(/\n{2,3}/g, '<br><br>');
  processed = processed.replace(/\n/g, '<br>');
  
  // Restore the anchor blocks we masked above.
  processed = processed.replace(/\u0000ANC(\d+)\u0000/g, (_m, i) => __anchorMasks[Number(i)]);
  
  return processed;
};

export const generateStoryHtml = (twee, layoutOption = 'bottom', styleColors = {}, linkDisplay = 'buttons', enableIntroMode = false, navOption = 'full', maxPassageLength = 0) => {
  // Parse Twee format
  let passages = parseTwee(twee);
  
  // Split long passages if max length is set
  if (maxPassageLength > 0) {
    passages = splitLongPassages(passages, maxPassageLength);
  }
  
  let startPassage = passages.find(p => p.name === 'Start');
  const storyTitle = passages.find(p => p.name === 'StoryTitle')?.content.trim() || 'Interactive Story';
  
  // Extract style colors with defaults
  const { 
    boldColor = '#fbd38d', 
    italicColor = '#b794f4', 
    headingColor = '#90cdf4', 
    linkColor = '#667eea',
    textBgColor = '#ffffff',
    frameBgColor = '#f8f9fa'
  } = styleColors;
  
  // If no Start passage, use the first non-StoryTitle passage
  if (!startPassage) {
    startPassage = passages.find(p => p.name !== 'StoryTitle');
  }
  
  if (!startPassage) {
    throw new Error('No valid passages found. Make sure you have a ":: Start" passage.');
  }

  // Filter out StoryTitle from passages used in navigation
  const storyPassages = passages.filter(p => p.name !== 'StoryTitle');
  
  // Ensure Start passage is first for CSS :first-of-type to work
  storyPassages.sort((a, b) => {
    if (a.name === startPassage.name) return -1;
    if (b.name === startPassage.name) return 1;
    return 0;
  });

  // Generate pure HTML with JavaScript for navigation
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${storyTitle}</title>
  <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body {
          height: 100%;
          margin: 0;
          padding: 0;
      }
      body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          line-height: 1.6;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
          min-height: 100vh;
          box-sizing: border-box;
      }
      
      /* Story Container */
      #story-container {
          background: ${frameBgColor};
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          display: flex;
          flex-direction: column;
          max-width: 900px;
          margin: 0 auto;
          width: 100%;
          min-height: min(600px, calc(100vh - 40px));
          max-height: calc(100vh - 40px);
      }
      
      /* Reduce container margins on small screens */
      @media (max-width: 480px) {
          #story-container {
              border-radius: 8px;
              min-height: 500px;
          }
          body {
              padding: 10px;
          }
      }
      
      @media (max-width: 320px) {
          #story-container {
              border-radius: 6px;
              min-height: 450px;
          }
          body {
              padding: 5px;
          }
      }
      
      /* Story Header */
      .story-header {
          padding: 24px 40px 16px;
          border-bottom: 1px solid #e2e8f0;
      }
      .story-title {
          color: #667eea;
          font-size: 1.8em;
          font-weight: 700;
          margin-bottom: 12px;
          line-height: 1.3;
      }
      
      /* Navigation Controls */
      #nav-controls {
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 0.85em;
          color: #64748b;
          margin-bottom: 8px;
      }
      .nav-link {
          cursor: pointer;
          color: #667eea;
          text-decoration: none;
          transition: all 0.2s;
      }
      .nav-link:hover {
          color: #5568d3;
          text-decoration: underline;
      }
      .nav-link.disabled {
          color: #cbd5e1;
          cursor: not-allowed;
          pointer-events: none;
      }
      .nav-separator {
          color: #cbd5e1;
      }
      
      /* Breadcrumb */
      #breadcrumb {
          font-size: 0.8em;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
      }
      .breadcrumb-item {
          cursor: pointer;
          color: #667eea;
          text-decoration: none;
      }
      .breadcrumb-item:hover {
          text-decoration: underline;
      }
      .breadcrumb-sep {
          color: #cbd5e1;
      }
      
      /* Content Area */
      .story-content-wrapper {
          overflow-y: auto;
          padding: 32px 40px;
          min-height: 200px;
          flex: 1;
          position: relative;
      }
      
      /* Scroll indicator - bottom fade gradient with arrow hint */
      .story-content-wrapper::after {
          content: '⌄ Scroll down ⌄';
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          height: 80px;
          background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 40%, rgba(255,255,255,1) 100%);
          pointer-events: none;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          margin-top: -80px;
          z-index: 1;
          font-size: 0.85em;
          color: #667eea;
          font-weight: 600;
          padding-bottom: 12px;
          animation: bounce 2s infinite;
      }
      
      @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
          }
          40% {
              transform: translateY(-5px);
          }
          60% {
              transform: translateY(-3px);
          }
      }
      
      /* Hide scroll indicator when at bottom */
      .story-content-wrapper.at-bottom::after {
          display: none;
      }
      /* Passage Display */
      .passage {
          display: none;
      }
      .passage.active {
          display: block;
          animation: fadeIn 0.3s ease-in;
      }
      @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
      }
      
      /* Link Effect Animations */
      @keyframes pulse {
          0%, 100% { 
              opacity: 1; 
              transform: scale(1);
          }
          50% { 
              opacity: 0.8; 
              transform: scale(1.05);
          }
      }
      
      @keyframes glow {
          0%, 100% { 
              box-shadow: 0 0 5px rgba(102, 126, 234, 0.5);
          }
          50% { 
              box-shadow: 0 0 20px rgba(102, 126, 234, 0.8), 0 0 30px rgba(102, 126, 234, 0.6);
          }
      }
      
      .link-effect-pulse {
          animation: pulse 2s ease-in-out infinite;
      }
      
      .link-effect-highlight {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 193, 7, 0.3));
          box-shadow: 0 2px 8px rgba(255, 193, 7, 0.4);
          font-weight: 600;
      }
      
      .link-effect-glow {
          animation: glow 2s ease-in-out infinite;
      }
      
      .passage-title {
          display: none;
      }
      .passage-content {
          color: #2d3748;
          background: ${textBgColor};
          font-size: 1.1em;
          margin-bottom: 24px;
          line-height: 1.8;
          padding: 16px;
          border-radius: 8px;
      }
      .passage-content h3, .passage-content h4, .passage-content h5, .passage-content h6 {
          color: ${headingColor};
          font-weight: 600;
          margin: 16px 0 8px 0;
      }
      .passage-content h3 { font-size: 1.5em; }
      .passage-content h4 { font-size: 1.3em; }
      .passage-content h5 { font-size: 1.1em; }
      .passage-content strong {
          font-weight: 700;
          color: ${boldColor};
      }
      .passage-content em {
          font-style: italic;
          color: ${italicColor};
      }
      .passage-content u {
          text-decoration: underline;
      }
      .passage-content del {
          text-decoration: line-through;
          opacity: 0.7;
      }
      .passage-content hr {
          border: none;
          border-top: 2px solid #e2e8f0;
          margin: 20px 0;
      }
      .passage-content ul {
          margin: 12px 0;
          padding-left: 2.5em;  /* ~4 characters indent */
          line-height: 1.6;
      }
      .passage-content li {
          margin: 4px 0;
          line-height: 1.6;
      }
      /* Add extra spacing between paragraphs (after line breaks) */
      .passage-content br {
          display: block;
          content: "";
          margin-bottom: 0.4em;
      }
      
      /* Image Styling */
      .story-image {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 16px 0;
          display: block;
      }
      
      /* Image Positioning */
      .img-top {
          margin: 0 0 20px 0;
          width: 100%;
      }
      .img-bottom {
          margin: 20px 0 0 0;
          width: 100%;
      }
      .img-center {
          margin: 16px auto;
          display: block;
          max-width: 80%;
      }
      .img-left {
          float: left;
          max-width: 45%;
          margin: 0 20px 16px 0;
      }
      .img-right {
          float: right;
          max-width: 45%;
          margin: 0 0 16px 20px;
      }
      
      /* Passage Links Footer */
      .passage-links-footer {
          padding: 20px 40px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 0 0 16px 16px;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          min-height: 80px;
          max-height: 300px;
          align-items: flex-start;
          justify-content: flex-start;
          overflow-y: auto;
          flex-shrink: 0;
      }
      
      /* Side layout for options */
      .layout-side .passage-links-footer {
          position: fixed;
          right: 20px;
          top: 140px;
          bottom: 40px;
          width: 280px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          flex-direction: column;
          padding: 20px;
          gap: 12px;
          max-height: none;
          overflow-y: auto;
      }
      
      .layout-side #story-container {
          margin-right: 320px;
          max-width: calc(100% - 340px);
      }
      
      /* Better styling for side view passage links */
      .layout-side .passage-link {
          white-space: normal;
          text-align: left;
          padding: 12px 16px;
          line-height: 1.4;
          word-wrap: break-word;
      }
      
      /* Left/Right layout for options */
      .layout-leftRight #story-container {
          display: grid;
          grid-template-columns: 20% 60% 20%;
          grid-template-rows: auto 1fr;
          gap: 0;
          padding: 40px 20px;
          min-height: 400px;
          align-items: start;
      }
      
      .layout-leftRight .story-header {
          grid-column: 1 / -1;
          margin-bottom: 20px;
      }
      
      /* Left column */
      .layout-leftRight #passage-links-left {
          grid-column: 1;
          grid-row: 2;
          display: flex;
          flex-direction: column;
          gap: 15px;
          padding: 20px 15px;
          align-items: center;
          justify-content: center;
      }
      
      /* Center column */
      .layout-leftRight .story-content-wrapper {
          grid-column: 2;
          grid-row: 2;
          width: 100%;
          max-width: 100%;
          min-height: 300px;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          background: white;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          padding: 30px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin: 0 auto;
      }
      
      .layout-leftRight .story-content-wrapper h1 {
          font-size: 1.8em;
          margin-bottom: 15px;
          color: #667eea;
          text-align: center;
      }
      
      .layout-leftRight .story-content {
          font-size: 0.95em;
          line-height: 1.6;
          text-align: center;
          max-height: none;
      }
      
      /* Right column */
      .layout-leftRight #passage-links-right {
          grid-column: 3;
          grid-row: 2;
          display: flex;
          flex-direction: column;
          gap: 15px;
          padding: 20px 15px;
          align-items: center;
          justify-content: center;
      }
      
      .layout-leftRight .passage-links-footer {
          position: static;
          border: none;
          background: transparent;
          max-height: none;
      }
      
      .layout-leftRight .passage-link {
          width: 100%;
          max-width: 180px;
          text-align: center;
          padding: 16px 18px;
          font-size: 0.9em;
          white-space: normal;
          line-height: 1.4;
          min-height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
      }
      
      .layout-leftRight #passage-links-left .passage-link::before {
          content: '← ';
          opacity: 0.5;
          margin-right: 4px;
      }
      
      .layout-leftRight #passage-links-right .passage-link::after {
          content: ' →';
          opacity: 0.5;
          margin-left: 4px;
      }
      .passage-link {
          display: inline-block;
          background: ${linkColor};
          color: white;
          padding: 10px 16px;
          border-radius: 6px;
          text-decoration: none;
          transition: all 0.2s;
          font-weight: 500;
          cursor: pointer;
          border: none;
          font-family: inherit;
          font-size: 0.9em;
          max-width: 100%;
          text-align: center;
      }
      .passage-link:hover {
          background: ${linkColor};
          filter: brightness(0.85);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      }
      .passage-link:active {
          transform: translateY(0);
      }
      
      /* Inline passage links */
      .inline-passage-link {
          font-weight: 500;
          transition: all 0.2s;
          border-bottom: 2px solid currentColor;
      }
      .inline-passage-link:hover {
          opacity: 0.8;
          border-bottom-width: 3px;
      }
      
      /* Responsive adjustments for tablets and smaller */
      @media (max-width: 1024px) {
          .passage-link {
              font-size: 0.8em;
              padding: 9px 14px;
          }
      }
      
      /* Responsive adjustments for mobile */
      @media (max-width: 768px) {
          .img-left, .img-right {
              float: none;
              max-width: 100%;
              margin: 16px 0;
              display: block;
          }
          .story-header {
              padding: 16px 20px 10px;
          }
          .story-content-wrapper {
              padding: 20px;
              min-height: 250px;
              max-height: 500px;
          }
          .story-title {
              font-size: 1.3em;
          }
          .passage-links-footer {
              padding: 10px 12px;
              gap: 6px;
              max-height: 200px;
              min-height: 120px;
          }
          .passage-link {
              font-size: 0.75em;
              padding: 8px 10px;
              flex: 1 1 auto;
              min-width: calc(33.333% - 4px);
              max-width: calc(50% - 3px);
              border-radius: 4px;
          }
      }
      
      /* Small mobile screens */
      @media (max-width: 600px) {
          .story-header {
              padding: 12px 16px 8px;
          }
          .story-content-wrapper {
              padding: 16px;
              min-height: 200px;
              max-height: 400px;
          }
          .passage-links-footer {
              padding: 8px 10px;
              gap: 5px;
              max-height: 220px;
              min-height: 150px;
          }
          .passage-link {
              font-size: 0.7em;
              padding: 7px 9px;
              min-width: calc(50% - 2.5px);
              border-radius: 4px;
          }
      }
      
      /* Very small screens */
      @media (max-width: 480px) {
          .story-header {
              padding: 10px 12px 6px;
          }
          .story-content-wrapper {
              padding: 12px;
              min-height: 180px;
              max-height: 350px;
          }
          .passage-links-footer {
              padding: 8px 10px;
              gap: 5px;
              max-height: 200px;
              min-height: 140px;
          }
          .passage-link {
              font-size: 0.68em;
              padding: 7px 8px;
              min-width: calc(50% - 2.5px);
              border-radius: 3px;
          }
      }
      
      /* Side layout responsive - switch to bottom on tablets and smaller */
      @media (max-width: 1200px) {
          .layout-side .passage-links-footer {
              position: static;
              width: 100%;
              margin-top: 20px;
              border-radius: 12px;
          }
          .layout-side #story-container {
              margin-right: 0;
              max-width: 900px;
          }
          .layout-side .passage-link {
              text-align: center;
          }
          
          /* Left/Right responsive - stack on tablets */
          .layout-leftRight #story-container {
              grid-template-columns: 1fr;
              gap: 20px;
              padding: 20px 10px;
          }
          
          .layout-leftRight #passage-links-left,
          .layout-leftRight .story-content-wrapper,
          .layout-leftRight #passage-links-right {
              grid-column: 1;
          }
          
          .layout-leftRight .story-content-wrapper {
              width: 100%;
              max-width: 500px;
          }
          
          .layout-leftRight .passage-link {
              width: 100%;
              max-width: 300px;
          }
      }
      
      /* Extra small screens */
      @media (max-width: 380px) {
          .story-header {
              padding: 8px 10px 6px;
          }
          .story-content-wrapper {
              padding: 10px;
              min-height: 160px;
              max-height: 300px;
          }
          .passage-links-footer {
              padding: 8px 10px;
              gap: 4px;
              max-height: 180px;
              min-height: 130px;
          }
          .passage-link {
              font-size: 0.65em;
              padding: 6px 8px;
              letter-spacing: -0.2px;
              border-radius: 3px;
              min-width: calc(50% - 2px);
          }
      }
      
      /* Ultra small screens */
      @media (max-width: 320px) {
          .story-header {
              padding: 6px 8px 4px;
          }
          .story-content-wrapper {
              padding: 8px;
              min-height: 150px;
              max-height: 280px;
          }
          .passage-links-footer {
              padding: 8px 10px;
              gap: 4px;
              max-height: 170px;
              min-height: 120px;
          }
          .passage-link {
              font-size: 0.62em;
              padding: 6px 7px;
              letter-spacing: -0.3px;
              border-radius: 3px;
          }
      }
      
      /* Clear floats after content */
      .passage-content::after {
          content: "";
          display: table;
          clear: both;
      }
  </style>
</head>
<body>
  <div id="story-container" class="layout-${layoutOption}">
      <!-- Story Header -->
      <div class="story-header">
          <div class="story-title">${storyTitle}</div>
          ${navOption !== 'none' ? `
          <div id="nav-controls">
              <a class="nav-link" id="link-back" onclick="goBack()">← Back</a>
              ${navOption === 'full' ? `
              <span class="nav-separator">|</span>
              <a class="nav-link" id="link-forward" onclick="goForward()">Forward →</a>
              ` : ''}
              <span class="nav-separator">|</span>
              <a class="nav-link" id="link-reset" onclick="resetStory()">⟲ Reset</a>
          </div>
          ` : ''}
          <div id="breadcrumb"></div>
      </div>
      
      ${layoutOption === 'leftRight' ? `
      <!-- Left column -->
      <div class="passage-links-footer" id="passage-links-left">
          <!-- Left links -->
      </div>
      ` : ''}
      
      <!-- Scrollable Content Area -->
      <div class="story-content-wrapper">
      ${storyPassages.map((passage, index) => {
        const passageId = passage.name.replace(/\s+/g, '-');
        const isStart = index === 0;
        
        // Extract links from content
        const linkRegex = /\[\[([^\]]+)\]\]/g;
        const links = [];
        let match;
        
        while ((match = linkRegex.exec(passage.content)) !== null) {
          const linkText = match[1];
          // Support both SugarCube [[Text|Passage]] and Twine [[Text->Passage]] formats
          // Also support Unicode arrow → in addition to ASCII arrow ->
          let parts, label, target;
          if (linkText.includes('|')) {
            // SugarCube format: [[Display Text|PassageName]]
            parts = linkText.split('|');
            label = parts[0].trim();
            target = parts[1].trim();
          } else if (linkText.includes('→')) {
            // Unicode arrow format: [[Display Text→PassageName]]
            parts = linkText.split('→');
            label = parts[0].trim();
            target = parts[1].trim();
          } else if (linkText.includes('->')) {
            // ASCII arrow format: [[Display Text->PassageName]]
            parts = linkText.split('->');
            label = parts[0].trim();
            target = parts[1].trim();
          } else {
            // Simple format: [[PassageName]]
            label = linkText.trim();
            target = linkText.trim();
          }
          
          // Check for positioning markers and effects in label: {{{left}}}, {{{right}}}, {{{pulse}}}, {{{highlight}}}
          let position = 'default';
          let effect = 'none';
          let cleanLabel = label;
          
          if (label.includes('{{{left}}}')) {
            position = 'left';
            cleanLabel = cleanLabel.replace('{{{left}}}', '').trim();
          } else if (label.includes('{{{right}}}')) {
            position = 'right';
            cleanLabel = cleanLabel.replace('{{{right}}}', '').trim();
          }
          
          // Check for effect markers
          if (cleanLabel.includes('{{{pulse}}}')) {
            effect = 'pulse';
            cleanLabel = cleanLabel.replace('{{{pulse}}}', '').trim();
          } else if (cleanLabel.includes('{{{highlight}}}')) {
            effect = 'highlight';
            cleanLabel = cleanLabel.replace('{{{highlight}}}', '').trim();
          } else if (cleanLabel.includes('{{{glow}}}')) {
            effect = 'glow';
            cleanLabel = cleanLabel.replace('{{{glow}}}', '').trim();
          }
          
          // Check if target is an external URL.
          // Recognised schemes: http(s)://, mailto:, tel:, protocol-relative //,
          // plus the legacy explicit "URL:" prefix for back-compat.
          const isExternalLink = /^(URL:|https?:\/\/|mailto:|tel:|\/\/)/i.test(target);
          const finalTarget = isExternalLink
            ? target.replace(/^URL:/i, '')
            : target.replace(/\s+/g, '-');
          links.push({ label: cleanLabel, target: finalTarget, isExternal: isExternalLink, position, effect });
        }
        
        let content, linksHtml;
        
        if (linkDisplay === 'inline') {
          // Keep links in the text as inline clickable links
          content = passage.content.replace(linkRegex, (match, linkText) => {
            let parts, label, target, position = 'default', effect = 'none';
            
            // Parse link format
            if (linkText.includes('|')) {
              parts = linkText.split('|');
              label = parts[0].trim();
              target = parts[1].trim();
            } else if (linkText.includes('->')) {
              parts = linkText.split('->');
              label = parts[0].trim();
              target = parts[1].trim();
            } else {
              label = linkText.trim();
              target = linkText.trim();
            }
            
            // Remove position markers
            if (label.includes('{{{left}}}')) {
              label = label.replace('{{{left}}}', '').trim();
            } else if (label.includes('{{{right}}}')) {
              label = label.replace('{{{right}}}', '').trim();
            }
            
            // Remove and detect effect markers
            if (label.includes('{{{pulse}}}')) {
              effect = 'pulse';
              label = label.replace('{{{pulse}}}', '').trim();
            } else if (label.includes('{{{highlight}}}')) {
              effect = 'highlight';
              label = label.replace('{{{highlight}}}', '').trim();
            } else if (label.includes('{{{glow}}}')) {
              effect = 'glow';
              label = label.replace('{{{glow}}}', '').trim();
            }
            
            // Add effect class if present
            const effectClass = effect !== 'none' ? ` link-effect-${effect}` : '';
            
            // Handle external links (auto-detect common URL schemes; legacy "URL:" still works)
            const isExternalLink = /^(URL:|https?:\/\/|mailto:|tel:|\/\/)/i.test(target);
            const finalTarget = isExternalLink
              ? target.replace(/^URL:/i, '')
              : target.replace(/\s+/g, '-');
            
            if (isExternalLink) {
              return `<a href="${finalTarget}" target="_blank" rel="noopener noreferrer" class="inline-passage-link${effectClass}" style="color: ${styleColors.linkColor || '#667eea'}; text-decoration: underline; cursor: pointer;">${label}</a>`;
            }
            return `<a href="#" class="inline-passage-link${effectClass}" data-target="${finalTarget}" onclick="navigate('${finalTarget}'); return false;" style="color: ${styleColors.linkColor || '#667eea'}; text-decoration: underline; cursor: pointer;">${label}</a>`;
          });
          
          content = processContent(content.trim());
          linksHtml = ''; // No separate links for inline mode
          
        } else {
          // Button mode: external URL links render INLINE as <a> within the
          // prose; only internal-target links become bottom buttons.
          const linkColor = styleColors.linkColor || '#667eea';
          content = passage.content.replace(linkRegex, (m, linkContent) => {
            let label, target;
            if (linkContent.includes('->')) {
              [label, target] = linkContent.split('->').map(s => s.trim());
            } else if (linkContent.includes('|')) {
              [label, target] = linkContent.split('|').map(s => s.trim());
            } else {
              label = target = linkContent.trim();
            }
            const isExternalLink = /^(URL:|https?:\/\/|mailto:|tel:|\/\/|#|\/)/i.test(target);
            if (!isExternalLink) return m; // leave for stripping below
            const href = target.replace(/^URL:/i, '');
            return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="inline-passage-link" style="color: ${linkColor}; text-decoration: underline;">${label}</a>`;
          });
          // Strip remaining (internal) [[..]] links from prose
          content = content.replace(linkRegex, '').trim();
          
          // Remove lines that were ONLY bullet + link (now empty after link removal)
          content = content.split('\n').filter(line => {
            const trimmed = line.trim();
            return trimmed !== '' && trimmed !== '*' && trimmed !== '-';
          }).join('\n');
          
          content = processContent(content);
          
          linksHtml = links
            .filter(link => !link.isExternal)
            .map(link => {
              const positionAttr = `data-position="${link.position}"`;
              const effectClass = link.effect && link.effect !== 'none' ? ` link-effect-${link.effect}` : '';
              return `<button class="passage-link${effectClass}" ${positionAttr} onclick="navigate('${link.target}')">${link.label}</button>`;
            }).join('');
        }
        
        return `
      <div class="passage" id="${passageId}" data-name="${passage.name}"${isStart ? ' data-start="true"' : ''}>
          <div class="passage-content">${content}</div>
          <div class="passage-links-data" style="display:none;">${linksHtml}</div>
      </div>`;
      }).join('\n')}
      </div>
      
      <!-- Passage Links Footer (Always Visible) -->
      ${layoutOption === 'leftRight' ? `
      <!-- Right column -->
      <div class="passage-links-footer" id="passage-links-right">
          <!-- Right links -->
      </div>
      ` : `
      <div class="passage-links-footer" id="passage-links-container">
          <!-- Links will be dynamically inserted here -->
      </div>
      `}
  </div>

  <script>
      const layoutOption = '${layoutOption}';
      // Navigation history
      let history = [];
      let currentIndex = -1;
      
      // Initialize
      function init() {
          const startPassage = document.querySelector('[data-start="true"]');
          if (startPassage) {
              showPassage(startPassage.id, false);
          } else {
              // Fallback: if no passage marked as start, use the first one
              const firstPassage = document.querySelector('.passage');
              if (firstPassage) {
                  showPassage(firstPassage.id, false);
              }
          }
          
          // Add scroll listener for scroll indicator
          const contentWrapper = document.querySelector('.story-content-wrapper');
          if (contentWrapper) {
              contentWrapper.addEventListener('scroll', function() {
                  checkScrollIndicator(contentWrapper);
              });
          }
      }
      
      // Show specific passage
      function showPassage(passageId, addToHistory = true) {
          // Hide all passages
          document.querySelectorAll('.passage').forEach(p => p.classList.remove('active'));
          
          // Show target passage
          const passage = document.getElementById(passageId);
          if (passage) {
              passage.classList.add('active');
              
              // Update history
              if (addToHistory) {
                  currentIndex++;
                  history = history.slice(0, currentIndex);
                  history.push(passageId);
              }
              
              // Update passage links in footer
              updatePassageLinks(passage);
              
              updateBreadcrumb();
              updateButtons();
              
              // Scroll content to top
              const contentWrapper = document.querySelector('.story-content-wrapper');
              if (contentWrapper) {
                  contentWrapper.scrollTop = 0;
                  checkScrollIndicator(contentWrapper);
              }
          }
      }
      
      // Check if scroll indicator should be shown
      function checkScrollIndicator(wrapper) {
          if (!wrapper) return;
          
          const isScrollable = wrapper.scrollHeight > wrapper.clientHeight;
          const isAtBottom = wrapper.scrollHeight - wrapper.scrollTop <= wrapper.clientHeight + 10;
          
          if (isScrollable && !isAtBottom) {
              wrapper.classList.remove('at-bottom');
          } else {
              wrapper.classList.add('at-bottom');
          }
      }
      
      // Update passage links in footer
      function updatePassageLinks(passage) {
          const linksData = passage.querySelector('.passage-links-data');
          
          if (layoutOption === 'leftRight') {
              const leftContainer = document.getElementById('passage-links-left');
              const rightContainer = document.getElementById('passage-links-right');
              
              if (linksData) {
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = linksData.innerHTML;
                  const allLinks = Array.from(tempDiv.querySelectorAll('.passage-link'));
                  
                  // Sort by data-position attribute
                  const leftLinks = allLinks.filter(link => link.getAttribute('data-position') === 'left');
                  const rightLinks = allLinks.filter(link => link.getAttribute('data-position') === 'right');
                  const defaultLinks = allLinks.filter(link => link.getAttribute('data-position') === 'default');
                  
                  // If there are explicit left/right markers, use them
                  if (leftLinks.length > 0 || rightLinks.length > 0) {
                      leftContainer.innerHTML = leftLinks.map(link => link.outerHTML).join('');
                      rightContainer.innerHTML = rightLinks.map(link => link.outerHTML).join('');
                  } else {
                      // No explicit markers: distribute default links between left and right
                      const midpoint = Math.ceil(defaultLinks.length / 2);
                      const leftDefaultLinks = defaultLinks.slice(0, midpoint);
                      const rightDefaultLinks = defaultLinks.slice(midpoint);
                      
                      leftContainer.innerHTML = leftDefaultLinks.map(link => link.outerHTML).join('');
                      rightContainer.innerHTML = rightDefaultLinks.map(link => link.outerHTML).join('');
                  }
              } else {
                  leftContainer.innerHTML = '';
                  rightContainer.innerHTML = '';
              }
          } else {
              const linksContainer = document.getElementById('passage-links-container');
              if (linksData) {
                  linksContainer.innerHTML = linksData.innerHTML;
              } else {
                  linksContainer.innerHTML = '';
              }
          }
      }
      
      // Navigate to passage
      function navigate(passageId) {
          showPassage(passageId, true);
      }
      
      // Go back
      function goBack() {
          if (currentIndex > 0) {
              currentIndex--;
              showPassage(history[currentIndex], false);
          }
      }
      
      // Go forward
      function goForward() {
          if (currentIndex < history.length - 1) {
              currentIndex++;
              showPassage(history[currentIndex], false);
          }
      }
      
      // Reset to start
      function resetStory() {
          history = [];
          currentIndex = -1;
          init();
      }
      
      // Update breadcrumb
      function updateBreadcrumb() {
          const breadcrumb = document.getElementById('breadcrumb');
          if (!breadcrumb) return;
          
          const items = history.slice(Math.max(0, currentIndex - 3), currentIndex + 1);
          
          breadcrumb.innerHTML = items.map((passageId, index) => {
              const passage = document.getElementById(passageId);
              const name = passage ? passage.dataset.name : passageId;
              const isLast = index === items.length - 1;
              const actualIndex = Math.max(0, currentIndex - 3) + index;
              
              let html = '<a class="breadcrumb-item" onclick="jumpTo(' + actualIndex + ')" ';
              html += 'style="' + (isLast ? 'font-weight:600;' : '') + '">';
              html += name + '</a>';
              if (!isLast) {
                  html += '<span class="breadcrumb-sep">›</span>';
              }
              return html;
          }).join('');
      }
      
      // Jump to history index
      function jumpTo(index) {
          currentIndex = index;
          showPassage(history[currentIndex], false);
      }
      
      // Update button states
      function updateButtons() {
          const backLink = document.getElementById('link-back');
          const forwardLink = document.getElementById('link-forward');
          
          if (currentIndex <= 0) {
              backLink.classList.add('disabled');
          } else {
              backLink.classList.remove('disabled');
          }
          
          if (currentIndex >= history.length - 1) {
              forwardLink.classList.add('disabled');
          } else {
              forwardLink.classList.remove('disabled');
          }
      }
      
      /* Hamburger menu removed - no longer needed */
      
      // Initialize on load
      if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', init);
      } else {
          init();
      }
  </script>
</body>
</html>`;
};

